import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { HuddleSettings } from "@/components/HuddleSettings";
import { toast } from "sonner";
import { Loader2, Users, CheckCircle2, Mail } from "lucide-react";

const monthLabel = (key) => {
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
};
const shiftMonth = (key, by) => {
  const [y, m] = key.split("-").map(Number);
  const d = new Date(y, m - 1 + by, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const localWhen = (iso) => (iso ? new Date(iso).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" }) : "—");

export default function Huddle() {
  const [tab, setTab] = useState("attendance");
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = (key) => api.get("/huddle/attendance", { params: { month: key } })
    .then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(month); }, [month]);

  const mark = async (slot, ids, joined) => {
    setBusy(true);
    try {
      await api.post("/huddle/attendance", { month, slot, student_ids: ids, joined });
      await load(month);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="huddle-page">
      <PageHeader title="Student Huddle" subtitle="The monthly teaching session — who's invited, who joined, and how it's set up" />

      <div className="flex flex-wrap gap-2" data-testid="huddle-tabs">
        {[["attendance", "Attendance"], ["settings", "Settings"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`huddle-tab-${k}`}
            className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors ${tab === k ? "bg-cc-blue text-white border-cc-blue" : "bg-white text-zinc-600 hover:border-cc-blue"}`}>{label}</button>
        ))}
      </div>

      {tab === "settings" ? <HuddleSettings /> : !data ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-cc-blue" /></div>
      ) : (
        <div className="space-y-4">
          <Card>
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="outline" size="sm" onClick={() => setMonth(shiftMonth(month, -1))} data-testid="huddle-prev-month">← Previous</Button>
              <span className="font-semibold text-zinc-900 text-base" data-testid="huddle-month">{monthLabel(month)}</span>
              <Button variant="outline" size="sm" onClick={() => setMonth(shiftMonth(month, 1))} data-testid="huddle-next-month">Next →</Button>
              <Button variant="ghost" size="sm" onClick={() => setMonth(thisMonth())}>This month</Button>
              {data.skipped && <span className="text-xs text-amber-600 font-medium">This month is set to skip</span>}
              {!data.enabled && <span className="text-xs text-red-600 font-medium">The huddle is paused</span>}
              <span className="ml-auto text-xs text-zinc-400">Session times are shown in your local timezone</span>
            </div>
          </Card>

          {data.slots.map((s) => (
            <Card key={s.label} data-testid={`attendance-slot-${s.label}`}>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg bg-cc-blue/10 flex items-center justify-center"><Users className="w-4 h-4 text-cc-blue" /></div>
                <div>
                  <p className="font-semibold text-zinc-900">{s.label}</p>
                  <p className="text-xs text-zinc-500">{s.occurrence ? localWhen(s.occurrence.start) : "No session this month"}</p>
                </div>
                <div className="ml-auto flex flex-wrap items-center gap-4 text-sm">
                  <span className="flex items-center gap-1.5 text-zinc-500" data-testid={`invited-count-${s.label}`}><Mail className="w-4 h-4" />{s.invited_count} invited</span>
                  <span className="flex items-center gap-1.5 text-cc-green font-medium" data-testid={`joined-count-${s.label}`}><CheckCircle2 className="w-4 h-4" />{s.joined_count} joined</span>
                  {s.students.length > 0 && (
                    <Button size="sm" variant="outline" disabled={busy} data-testid={`mark-all-${s.label}`}
                      onClick={() => mark(s.label, s.students.map((r) => r.student_id), s.joined_count < s.students.length)}>
                      {s.joined_count < s.students.length ? "Mark all joined" : "Clear all"}
                    </Button>
                  )}
                </div>
              </div>
              {s.students.length === 0 ? (
                <EmptyState text="No students are on rotation for this session." />
              ) : (
                <div className="overflow-x-auto cc-scroll">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase">
                      <tr>
                        <th className="text-left px-3 py-2.5 w-20">Joined</th>
                        <th className="text-left px-3 py-2.5">Student</th>
                        <th className="text-left px-3 py-2.5">School</th>
                        <th className="text-left px-3 py-2.5">Rotation</th>
                        <th className="text-left px-3 py-2.5">Invite</th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.students.map((r) => (
                        <tr key={r.student_id} className="border-t" data-testid={`attendance-row-${r.student_id}`}>
                          <td className="px-3 py-2.5">
                            <Checkbox checked={r.joined} disabled={busy} onCheckedChange={(v) => mark(s.label, [r.student_id], !!v)}
                              data-testid={`attendance-check-${r.student_id}`} />
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="font-medium text-zinc-800">{r.student_name}</div>
                            <div className="text-xs text-zinc-400">{r.discipline}{r.marked_by ? ` · marked by ${r.marked_by}` : ""}</div>
                          </td>
                          <td className="px-3 py-2.5 text-zinc-600">{r.school_name || "—"}</td>
                          <td className="px-3 py-2.5 text-xs font-mono text-zinc-500">{r.rotation_start} → {r.rotation_end}</td>
                          <td className="px-3 py-2.5 text-xs">
                            {r.invited ? <span className="text-cc-green">sent</span> : <span className="text-zinc-400">not sent yet</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
